import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  SendHorizontal,
  CheckCircle2,
  Eye,
  RotateCcw,
  Loader2,
  Printer,
  FileText,
} from "lucide-react";
import { colors, fonts, DOCUMENT_STATUSES, MERGE_TAGS } from "../styles/theme";
import { Button } from "../components/ui";
import { useTenant } from "../contexts/TenantContext";
import { fetchDocument, saveDocument, sendDocument, sendNda } from "../services/api";
import { getDocTemplates } from "../data/doc-templates";
import EmailEditor from "../components/EmailEditor";
import CompanyContactPicker from "../components/CompanyContactPicker";

function StatusBadge({ status }) {
  const cfg = DOCUMENT_STATUSES[status] || DOCUMENT_STATUSES.draft;
  return (
    <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

export default function DocEditor() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tenant = useTenant();
  const editorRef = useRef(null);

  const docId = params.get("id");
  const isNew = !docId;

  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [templateId, setTemplateId] = useState("blank");
  const [companyName, setCompanyName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [tags, setTags] = useState("");
  const [status, setStatus] = useState("draft");
  const [bodyHtml, setBodyHtml] = useState("");
  const [loading, setLoading] = useState(!!docId);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const templates = getDocTemplates(tenant);

  // Load existing document
  useEffect(() => {
    if (!docId) return;
    setLoading(true);
    fetchDocument(docId)
      .then((d) => {
        setDoc(d);
        setTitle(d.title || "");
        setTemplateId(d.templateId || "blank");
        setCompanyName(d.companyName || "");
        setContactEmail(d.contactEmail || "");
        setTags((d.tags || []).join(", "));
        setStatus(d.status || "draft");
        setBodyHtml(d.bodyHtml || "");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [docId]);

  // Template selection (new documents only)
  const handleTemplateChange = useCallback((tid) => {
    setTemplateId(tid);
    const tmpl = templates.find((t) => t.id === tid);
    if (tmpl && isNew) {
      setBodyHtml(tmpl.defaultContent);
      editorRef.current?.setContent(tmpl.defaultContent);
      if (!title && tmpl.defaultSubject) setTitle(tmpl.defaultSubject);
    }
  }, [templates, isNew, title]);

  // Save
  const handleSave = async (newStatus) => {
    const html = editorRef.current?.getHTML() || bodyHtml;
    if (!title.trim()) { setError("Title is required"); return; }

    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        ...(doc || {}),
        id: doc?.id || undefined,
        type: "document",
        title: title.trim(),
        templateId,
        bodyHtml: html,
        status: newStatus || status,
        companyName,
        contactEmail,
        tags: tagList,
      };
      if (newStatus) payload.statusNote = `Status changed to ${newStatus}`;
      const result = await saveDocument(payload);
      setDoc(result);
      setStatus(result.status);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      // Update URL if first save
      if (isNew && result.id) {
        navigate(`/doc-editor?id=${result.id}`, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Send
  const handleSend = async () => {
    if (!contactEmail) { setError("A contact email is required to send"); return; }
    if (!window.confirm(`Send "${title}" to ${contactEmail}?`)) return;
    setSending(true);
    setError(null);
    try {
      // Save first to capture latest edits
      await handleSave("approved");
      await sendDocument(doc.id, {
        sendMethod: "sendgrid",
        recipientEmail: contactEmail,
      });
      setStatus("sent");
      setDoc((prev) => ({ ...prev, status: "sent" }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Print
  const handlePrint = useCallback(() => {
    const html = editorRef.current?.getHTML() || bodyHtml;
    const printWin = window.open("", "_blank");
    if (!printWin) return;
    printWin.document.write(`<!DOCTYPE html>
<html><head>
<title>${title || "Document"}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@700&display=swap');
  body { font-family: 'DM Sans', sans-serif; max-width: 700px; margin: 40px auto; padding: 0 24px; color: #1a1d26; line-height: 1.7; font-size: 14px; }
  h1, h2, h3 { font-family: 'Playfair Display', Georgia, serif; color: #000f2d; }
  h1 { font-size: 24px; } h2 { font-size: 20px; margin-top: 24px; } h3 { font-size: 17px; margin-top: 18px; }
  img { max-width: 100%; height: auto; }
  table { border-collapse: collapse; width: 100%; } th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
  @media print { body { margin: 0; } }
</style>
</head><body>
<h1>${title || ""}</h1>
${companyName ? `<p style="color:#5a6178;margin-bottom:24px">Prepared for: ${companyName}</p>` : ""}
${html}
</body></html>`);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => printWin.print(), 300);
  }, [bodyHtml, title, companyName]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <Loader2 size={24} color={colors.accent} className="spin" />      </div>
    );
  }

  const isSent = status === "sent";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 56px)" }}>
      {/* Top bar */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: `1px solid ${colors.border}`,
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate("/documents")}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer",
            fontSize: 13, color: colors.textSecondary, fontFamily: fonts.body,
          }}
        >
          <ArrowLeft size={16} /> Back to Documents
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StatusBadge status={status} />

          <Button variant="default" onClick={handlePrint} disabled={isSent}>
            <Printer size={14} /> Print
          </Button>

          {status === "draft" && (
            <>
              <Button variant="default" onClick={() => handleSave()} disabled={saving}>
                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                {saveSuccess ? "Saved!" : "Save Draft"}
              </Button>
              <Button variant="primary" onClick={() => handleSave("review")} disabled={saving}>
                <Eye size={14} /> Submit for Review
              </Button>
            </>
          )}
          {status === "review" && (
            <>
              <Button variant="default" onClick={() => handleSave("draft")} disabled={saving}>
                <RotateCcw size={14} /> Return to Draft
              </Button>
              <Button variant="primary" onClick={() => handleSave("approved")} disabled={saving}>
                <CheckCircle2 size={14} /> Approve
              </Button>
            </>
          )}
          {status === "approved" && (
            <>
              <Button variant="default" onClick={() => handleSave("draft")} disabled={saving}>
                <RotateCcw size={14} /> Revise
              </Button>
              <Button variant="primary" onClick={handleSend} disabled={sending}>
                {sending ? <Loader2 size={14} className="spin" /> : <SendHorizontal size={14} />}
                Send
              </Button>
            </>
          )}
          {isSent && (
            <Button variant="default" disabled>
              <CheckCircle2 size={14} /> Sent
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: "8px 12px", margin: "8px 0", borderRadius: 6, background: "rgba(239,68,68,0.1)", color: colors.danger, fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Main content */}
      <div style={{ display: "flex", flex: 1, gap: 24, overflow: "hidden", paddingTop: 16 }}>
        {/* Left sidebar */}
        <div style={{
          width: 260,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 16,
          overflow: "auto",
          paddingBottom: 24,
        }}>
          {/* Template select */}
          {isNew && (
            <div>
              <label style={labelStyle}>Template</label>
              <select
                value={templateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                style={selectStyle}
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label style={labelStyle}>Document Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter document title..."
              disabled={isSent}
              style={inputStyle}
            />
          </div>

          {/* Company/Contact */}
          <CompanyContactPicker
            companyName={companyName}
            contactEmail={contactEmail}
            onCompanyChange={setCompanyName}
            onContactChange={setContactEmail}
          />

          {/* Tags */}
          <div>
            <label style={labelStyle}>Tags (comma-separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. q1-2026, proposal"
              disabled={isSent}
              style={inputStyle}
            />
          </div>

          {/* Status history */}
          {doc?.statusHistory?.length > 0 && (
            <div>
              <label style={labelStyle}>History</label>
              <div style={{ fontSize: 12, color: colors.textSecondary }}>
                {doc.statusHistory.map((h, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    <StatusBadge status={h.status} />{" "}
                    <span style={{ fontSize: 11 }}>
                      {h.by?.split("@")[0]} &middot; {new Date(h.at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Editor or NDA panel */}
        {templateId === "nda" && isNew ? (
          <NdaSendPanel
            companyName={companyName}
            contactEmail={contactEmail}
            onDone={() => navigate("/documents")}
          />
        ) : (
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            overflow: "hidden",
            background: colors.bgCard,
          }}>
            <EmailEditor
              ref={editorRef}
              content={bodyHtml}
              onUpdate={setBodyHtml}
              placeholder="Start writing your document..."
              mergeTags={MERGE_TAGS}
            />
          </div>
        )}
      </div>    </div>
  );
}

function NdaSendPanel({ companyName, contactEmail, onDone }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [ndaError, setNdaError] = useState(null);

  const handleSend = async () => {
    if (!contactEmail) {
      setNdaError("Select a contact with an email address first.");
      return;
    }
    if (!window.confirm(`Send NDA for signature to ${contactEmail}?`)) return;
    setSending(true);
    setNdaError(null);
    try {
      await sendNda({ contactEmail, companyName });
      setSent(true);
    } catch (err) {
      setNdaError(err.message || "Failed to send NDA");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      border: `1px solid ${colors.border}`,
      borderRadius: 8,
      background: colors.bgCard,
      padding: 40,
      gap: 16,
    }}>
      <FileText size={48} color={colors.accent} style={{ opacity: 0.5 }} />
      <h2 style={{ fontFamily: fonts.heading, fontSize: 20, color: colors.text, margin: 0 }}>
        Send NDA for E-Signature
      </h2>
      <p style={{ color: colors.textSecondary, fontSize: 14, textAlign: "center", maxWidth: 400, margin: 0 }}>
        {sent
          ? "The NDA has been sent. The contact will receive an email from SignWell with a link to sign the document."
          : "Select a company and contact in the sidebar, then click below to send the standard NDA for digital signature via SignWell."}
      </p>

      {ndaError && (
        <div style={{ padding: "8px 16px", borderRadius: 6, background: "rgba(239,68,68,0.1)", color: colors.danger, fontSize: 13 }}>
          {ndaError}
        </div>
      )}

      {sent ? (
        <Button variant="primary" onClick={onDone}>
          <CheckCircle2 size={14} /> Back to Documents
        </Button>
      ) : (
        <Button variant="primary" onClick={handleSend} disabled={sending || !contactEmail}>
          {sending ? (
            <Loader2 size={14} className="spin" />
          ) : (
            <SendHorizontal size={14} />
          )}
          {sending ? "Sending..." : "Send NDA for Signature"}
        </Button>
      )}

      {!contactEmail && !sent && (
        <p style={{ color: colors.warning, fontSize: 12, margin: 0 }}>
          Please select a contact with an email address in the sidebar.
        </p>
      )}
    </div>
  );
}

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: colors.textSecondary,
  marginBottom: 4,
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

const selectStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  cursor: "pointer",
  boxSizing: "border-box",
};
